/*
 * Copyright (c) 2022. Dave van Rijn Development
 */

import React, {createContext, CSSProperties, forwardRef, PropsWithChildren, ReactElement, ReactNode} from 'react';
import InfiniteLoader from 'react-window-infinite-loader';
import {FixedSizeList} from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';

export type RenderRowProps = { index: number; style: CSSProperties; };
export type ListLoader = (startIndex: number) => Promise<void>;

interface Props<T> {
    onLoadMore: ListLoader;
    renderRow: (props: RenderRowProps) => ReactElement;
    hasMore: boolean;
    isLoadingMore: boolean;
    items: T[];
    itemSize: number;
    width?: string;
}

export default function LoadableList<T>(props: Props<T>) {
    const {items, hasMore, onLoadMore, isLoadingMore, renderRow, itemSize} = props;

    function isLoaded(index: number) {
        return !hasMore || index < items.length;
    }

    const itemCount = hasMore ? items.length + 1 : items.length;
    const _onLoadMore = isLoadingMore ? () => {
    } : onLoadMore;

    return (
        <AutoSizer>
            {({height, width}) => (
                <InfiniteLoader isItemLoaded={isLoaded} itemCount={itemCount} loadMoreItems={_onLoadMore}>
                    {({onItemsRendered, ref}) => (
                        <FixedSizeList width={props.width ?? width} height={height} itemCount={items.length} ref={ref}
                                       onItemsRendered={onItemsRendered} itemSize={itemSize}>
                            {renderRow}
                        </FixedSizeList>
                    )}
                </InfiniteLoader>
            )}
        </AutoSizer>
    )
}

const StickyListContext = createContext({
    stickyIndices: [] as Array<number>,
    ItemRenderer: (() => {}) as (props: RenderRowProps) => ReactNode,
});
StickyListContext.displayName = "StickyListContext";

function ItemWrapper<T>({data, index, style}: RenderRowProps & { data: any }) {
    const {ItemRenderer, stickyIndices} = data;
    if (stickyIndices && stickyIndices.includes(index)) {
        return null;
    }
    return <ItemRenderer index={index} style={style}/>;
}


interface HeaderListProps<T> extends Props<T> {
    renderStickyRow: (props: RenderRowProps) => ReactElement;
    stickyRowIndexes: Array<number>;
}

export function LoadableHeaderList<T>(props: HeaderListProps<T>) {
    const {
        items, hasMore, onLoadMore, isLoadingMore, renderRow, itemSize, renderStickyRow, stickyRowIndexes,
    } = props;
    const itemCount = hasMore ? items.length + 1 : items.length;
    const _onLoadMore = isLoadingMore ? () => {
    } : onLoadMore;

    function isLoaded(index: number) {
        return !hasMore || index < items.length;
    }

    const innerElementType = forwardRef(({children, ...rest}: PropsWithChildren<any>, ref: any) => (
        <StickyListContext.Consumer>
            {({stickyIndices}) => (
                <div ref={ref} {...rest}>
                    {stickyIndices.map(index => {
                        const style: CSSProperties = {
                            top: index * props.itemSize, left: 0, width: '100%', height: props.itemSize
                        };
                        return renderStickyRow({style, index});
                    })}
                    {children}
                </div>
            )}
        </StickyListContext.Consumer>
    ));

    return (
        <StickyListContext.Provider value={{ItemRenderer: renderRow, stickyIndices: props.stickyRowIndexes}}>
            <AutoSizer>
                {({height, width}) => (
                    <InfiniteLoader isItemLoaded={isLoaded} itemCount={itemCount} loadMoreItems={_onLoadMore}>
                        {({onItemsRendered, ref}) => (
                            <FixedSizeList width={props.width ?? width} height={height} itemCount={items.length}
                                           ref={ref} innerElementType={innerElementType}
                                           itemData={{ItemRenderer: renderRow, stickyIndices: stickyRowIndexes}}
                                           onItemsRendered={onItemsRendered} itemSize={itemSize}>
                                {ItemWrapper}
                            </FixedSizeList>
                        )}
                    </InfiniteLoader>
                )}
            </AutoSizer>
        </StickyListContext.Provider>
    )
}